/**
 * @description Estimate API service — CRUD, status actions, conversion, utilities
 *
 * Key features:
 * - Full CRUD: getEstimates, getEstimate, createEstimate, updateEstimate, deleteEstimate
 * - Status actions: sendEstimate, acceptEstimate, declineEstimate
 * - Conversion: convertToInvoice
 * - Utilities: getNextEstimateNumber, getStatistics, formatStatus
 *
 * @dependencies
 * - apiClient: axios instance with auth headers and base URL
 *
 * @notes
 * - Mirrors invoiceService.js patterns
 * - No payment-related methods (estimates don't track payments)
 * - Email sending via sendEstimate(number, emailData) — Phase 3
 * - No PDF methods yet (deferred to Phase 4)
 */

import apiClient from './apiClient';

const estimateService = {
  /**
   * Get all estimates with optional filters
   * @param {Object} params - Query parameters (status, customerId, dateFrom, dateTo, search, page, limit)
   * @returns {Promise} API response with estimates and pagination
   */
  getEstimates: async (params = {}) => {
    const response = await apiClient.get('/estimates', { params });
    return response.data;
  },

  /**
   * Get estimate statistics
   * @returns {Promise} API response with statistics by status + win rate
   */
  getStatistics: async () => {
    const response = await apiClient.get('/estimates/statistics');
    return response.data;
  },

  /**
   * Get next estimate number preview
   * @returns {Promise} API response with next estimate number
   */
  getNextEstimateNumber: async () => {
    const response = await apiClient.get('/estimates/next-number');
    return response.data;
  },

  /**
   * Get a specific estimate by estimate number
   * @param {string} estimateNumber - Estimate number (e.g., "EST-0001")
   * @returns {Promise} API response with estimate details and items
   */
  getEstimate: async (estimateNumber) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.get(`/estimates/${estimateNumber}`);
    return response.data;
  },

  /**
   * Live evaluate for estimate form (product rules only; server forces isRecurring=false).
   * @param {Object} payload
   * @returns {Promise} { appliedDiscounts, discountTotalCents, totalCents, codeError }
   */
  evaluateDiscounts: async (payload) => {
    const response = await apiClient.post('/discounts/evaluate', {
      context: 'estimate',
      isRecurring: false,
      items: payload.items || [],
      subtotalCents: payload.subtotalCents,
      code: payload.code
    });
    return response.data;
  },

  /**
   * Create a new estimate
   * @param {Object} estimateData - Estimate data including items array
   * @returns {Promise} API response with created estimate
   */
  createEstimate: async (estimateData) => {
    const response = await apiClient.post('/estimates', estimateData);
    return response.data;
  },

  /**
   * Update an existing estimate (draft only)
   * @param {string} estimateNumber - Estimate number
   * @param {Object} data - Updated estimate data including items
   * @returns {Promise} API response with updated estimate
   */
  updateEstimate: async (estimateNumber, data) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.put(`/estimates/${estimateNumber}`, data);
    return response.data;
  },

  /**
   * Delete an estimate (soft delete)
   * @param {string} estimateNumber - Estimate number
   * @returns {Promise} API response
   */
  deleteEstimate: async (estimateNumber) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.delete(`/estimates/${estimateNumber}`);
    return response.data;
  },

  /**
   * Send estimate — transitions draft → sent, or re-sends from sent
   * Optionally sends email if emailData is provided
   * @param {string} estimateNumber - Estimate number
   * @param {Object} [emailData] - Optional email payload { toEmail, ccEmails, bccEmails, subject, body, sendCopy }
   * @returns {Promise} API response with updated estimate
   */
  sendEstimate: async (estimateNumber, emailData = {}) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.post(`/estimates/${estimateNumber}/send`, emailData);
    return response.data;
  },

  /**
   * Accept estimate — transitions sent → accepted
   * @param {string} estimateNumber - Estimate number
   * @returns {Promise} API response with updated estimate
   */
  acceptEstimate: async (estimateNumber) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.post(`/estimates/${estimateNumber}/accept`);
    return response.data;
  },

  /**
   * Decline estimate — transitions sent → declined
   * @param {string} estimateNumber - Estimate number
   * @param {string} [reason] - Optional decline reason
   * @returns {Promise} API response with updated estimate
   */
  declineEstimate: async (estimateNumber, reason) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.post(`/estimates/${estimateNumber}/decline`, { reason });
    return response.data;
  },

  /**
   * Convert estimate to invoice
   * @param {string} estimateNumber - Estimate number
   * @returns {Promise} API response with { estimate, invoice }
   */
  convertToInvoice: async (estimateNumber) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.post(`/estimates/${estimateNumber}/convert`);
    return response.data;
  },

  /**
   * Clone an estimate as a new draft
   * @param {string} estimateNumber - Estimate number to clone
   * @returns {Promise} API response with new cloned estimate
   */
  cloneEstimate: async (estimateNumber) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.post(`/estimates/${estimateNumber}/clone`);
    return response.data;
  },

  /**
   * Download estimate PDF as blob
   * @param {string} estimateNumber - Estimate number
   * @returns {Promise<Blob>} PDF file blob
   */
  downloadPdf: async (estimateNumber) => {
    if (!estimateNumber) {
      throw new Error('Estimate number is required');
    }
    const response = await apiClient.get(`/estimates/${estimateNumber}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Format estimate status for display
   * @param {string} status - Estimate status
   * @returns {string} Formatted status text
   */
  formatStatus: (status) => {
    const statusMap = {
      'draft': 'Draft',
      'sent': 'Sent',
      'accepted': 'Accepted',
      'declined': 'Declined',
      'expired': 'Expired',
      'converted': 'Converted'
    };
    return statusMap[status] || status;
  },

  /**
   * Get status color class for styling
   * @param {string} status - Estimate status
   * @param {boolean} isExpired - Whether estimate is past expiration
   * @returns {string} Tailwind color classes
   */
  getStatusColor: (status, isExpired = false) => {
    if (isExpired && status === 'sent') {
      return 'bg-orange-100 text-orange-700';
    }
    const colorMap = {
      'draft': 'bg-gray-100 text-gray-700',
      'sent': 'bg-blue-100 text-blue-700',
      'accepted': 'bg-green-100 text-green-700',
      'declined': 'bg-red-100 text-red-700',
      'expired': 'bg-orange-100 text-orange-700',
      'converted': 'bg-purple-100 text-purple-700'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700';
  },

  /**
   * Format currency
   * @param {number} amount - Amount to format
   * @returns {string} Formatted amount
   */
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }
};

export default estimateService;
